import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Pagination,
  Stack,
  SelectChangeEvent,
} from '@mui/material';
import {
  Visibility,
  Download,
  FilterAltOff,
  Login,
  Logout,
  Add,
  Edit,
  Delete,
  Visibility as ViewIcon,
  RocketLaunch,
  Block,
  CheckCircle,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useNotification } from '../contexts/NotificationContext';
import { auditAPI, AuditLog } from '../services/api';

interface AuditLogViewerProps {
  tenantId?: string;
  userId?: string;
  resourceType?: string;
  pageSize?: number;
}

const ACTION_TYPES = [
  'login',
  'logout',
  'create',
  'read',
  'update',
  'delete',
  'deploy',
  'suspend',
  'activate',
];

const RESOURCE_TYPES = [
  'tenant',
  'project',
  'dataset',
  'user',
  'group',
  'deployment',
  'session',
];

export const AuditLogViewer: React.FC<AuditLogViewerProps> = ({
  tenantId,
  userId,
  resourceType,
  pageSize = 50,
}) => {
  const { isDark } = useTheme();
  const { showNotification } = useNotification();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

  // Filters
  const [filterAction, setFilterAction] = useState('');
  const [filterResourceType, setFilterResourceType] = useState(resourceType || '');
  const [filterUserId, setFilterUserId] = useState(userId || '');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterStatusCode, setFilterStatusCode] = useState('');

  useEffect(() => {
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, page, filterAction, filterResourceType, filterUserId, filterDateFrom, filterDateTo, filterStatusCode]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const response = await auditAPI.query({
        user_id: filterUserId || undefined,
        action: filterAction || undefined,
        action_category: filterResourceType || undefined,
        status_code_min: filterStatusCode ? parseInt(filterStatusCode) : undefined,
        date_from: filterDateFrom || undefined,
        date_to: filterDateTo || undefined,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });

      setLogs(response.logs);
      setTotalCount(response.total_count);
    } catch (error: any) {
      console.error('Load audit logs error:', error);
      showNotification('Failed to load audit logs', 'error');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilterAction('');
    setFilterResourceType(resourceType || '');
    setFilterUserId(userId || '');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterStatusCode('');
    setPage(1);
  };

  const exportLogs = async () => {
    try {
      showNotification('Export functionality coming soon', 'info');
    } catch (error: any) {
      console.error('Export error:', error);
      showNotification('Failed to export audit logs', 'error');
    }
  };

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const getStatusColor = (statusCode?: number): 'success' | 'warning' | 'error' | 'default' => {
    if (!statusCode) return 'default';
    if (statusCode >= 200 && statusCode < 300) return 'success';
    if (statusCode >= 300 && statusCode < 400) return 'warning';
    if (statusCode >= 400) return 'error';
    return 'default';
  };

  const getActionIcon = (action: string): React.ReactElement | undefined => {
    switch (action.toLowerCase()) {
      case 'login': return <Login sx={{ fontSize: '0.9rem' }} />;
      case 'logout': return <Logout sx={{ fontSize: '0.9rem' }} />;
      case 'create': return <Add sx={{ fontSize: '0.9rem' }} />;
      case 'read': return <ViewIcon sx={{ fontSize: '0.9rem' }} />;
      case 'update': return <Edit sx={{ fontSize: '0.9rem' }} />;
      case 'delete': return <Delete sx={{ fontSize: '0.9rem' }} />;
      case 'deploy': return <RocketLaunch sx={{ fontSize: '0.9rem' }} />;
      case 'suspend': return <Block sx={{ fontSize: '0.9rem' }} />;
      case 'activate': return <CheckCircle sx={{ fontSize: '0.9rem' }} />;
      default: return undefined;
    }
  };

  const getActionColor = (action: string): 'success' | 'info' | 'warning' | 'error' | 'default' => {
    switch (action.toLowerCase()) {
      case 'create': return 'success';
      case 'read': return 'info';
      case 'update': return 'warning';
      case 'delete': return 'error';
      case 'deploy': return 'info';
      case 'suspend': return 'warning';
      case 'activate': return 'success';
      default: return 'default';
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const cardStyles = {
    borderRadius: '16px',
    bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
    overflow: 'hidden',
  };

  return (
    <Box>
      {/* Filters Section */}
      <Box sx={{ ...cardStyles, p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography
            sx={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
              letterSpacing: '0.8px',
              textTransform: 'uppercase',
            }}
          >
            Filters
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Download />}
            onClick={exportLogs}
            sx={{
              borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
              color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
              '&:hover': {
                borderColor: '#32828B',
                bgcolor: 'rgba(50, 130, 139, 0.1)',
              },
            }}
          >
            Export
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Action</InputLabel>
            <Select
              value={filterAction}
              label="Action"
              onChange={(e: SelectChangeEvent) => {
                setFilterAction(e.target.value);
                setPage(1);
              }}
              disabled={loading}
            >
              <MenuItem value="">All Actions</MenuItem>
              {ACTION_TYPES.map((action) => (
                <MenuItem key={action} value={action}>
                  {action.charAt(0).toUpperCase() + action.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Resource</InputLabel>
            <Select
              value={filterResourceType}
              label="Resource"
              onChange={(e: SelectChangeEvent) => {
                setFilterResourceType(e.target.value);
                setPage(1);
              }}
              disabled={loading}
            >
              <MenuItem value="">All Resources</MenuItem>
              {RESOURCE_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filterStatusCode}
              label="Status"
              onChange={(e: SelectChangeEvent) => {
                setFilterStatusCode(e.target.value);
                setPage(1);
              }}
              disabled={loading}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="200">2xx Success</MenuItem>
              <MenuItem value="400">4xx Error</MenuItem>
              <MenuItem value="500">5xx Error</MenuItem>
            </Select>
          </FormControl>

          <TextField
            size="small"
            type="date"
            label="From"
            InputLabelProps={{ shrink: true }}
            value={filterDateFrom}
            onChange={(e) => {
              setFilterDateFrom(e.target.value);
              setPage(1);
            }}
            disabled={loading}
            sx={{ width: 150 }}
          />

          <TextField
            size="small"
            type="date"
            label="To"
            InputLabelProps={{ shrink: true }}
            value={filterDateTo}
            onChange={(e) => {
              setFilterDateTo(e.target.value);
              setPage(1);
            }}
            disabled={loading}
            sx={{ width: 150 }}
          />

          <Button
            variant="text"
            size="small"
            startIcon={<FilterAltOff />}
            onClick={clearFilters}
            disabled={loading}
            sx={{
              color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
            }}
          >
            Clear
          </Button>
        </Box>
      </Box>

      {/* Logs Table */}
      <Box sx={cardStyles}>
        {loading && logs.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#32828B' }} />
          </Box>
        ) : logs.length === 0 ? (
          <Alert
            severity="info"
            sx={{
              m: 3,
              borderRadius: '12px',
              bgcolor: 'rgba(50, 130, 139, 0.1)',
              border: '1px solid rgba(50, 130, 139, 0.2)',
            }}
          >
            No audit logs found. Try adjusting your filters.
          </Alert>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600, color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)' }}>
                      Timestamp
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)' }}>
                      User
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)' }}>
                      Action
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)' }}>
                      Resource
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)' }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)' }}>
                      IP Address
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)' }} align="center">
                      Details
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow
                      key={log.id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                        },
                      }}
                      onClick={() => setSelectedLog(log)}
                    >
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: '0.8rem',
                            fontFamily: 'monospace',
                            color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                          }}
                        >
                          {formatTimestamp(log.timestamp)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: '0.85rem',
                            fontWeight: 500,
                            color: isDark ? '#ffffff' : '#0f172a',
                          }}
                        >
                          {log.username}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getActionIcon(log.action)}
                          label={log.action.toUpperCase()}
                          size="small"
                          color={getActionColor(log.action)}
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: 24,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography
                            sx={{
                              fontSize: '0.85rem',
                              color: isDark ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)',
                            }}
                          >
                            {log.action_category || 'N/A'}
                          </Typography>
                          {log.request_path && (
                            <Typography
                              sx={{
                                fontSize: '0.7rem',
                                fontFamily: 'monospace',
                                color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: 200,
                              }}
                            >
                              {log.request_path}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.response_status || 'N/A'}
                          size="small"
                          color={getStatusColor(log.response_status)}
                          variant="outlined"
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            height: 22,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: '0.8rem',
                            fontFamily: 'monospace',
                            color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                          }}
                        >
                          {log.ip_address || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedLog(log);
                          }}
                          sx={{
                            color: '#32828B',
                            '&:hover': {
                              bgcolor: 'rgba(50, 130, 139, 0.1)',
                            },
                          }}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2,
                borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.8rem',
                  color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                }}
              >
                Showing {(page - 1) * pageSize + 1} - {Math.min(page * pageSize, totalCount)} of {totalCount} logs
              </Typography>
              <Stack spacing={2}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  disabled={loading}
                  size="small"
                  sx={{
                    '& .MuiPaginationItem-root': {
                      color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                    },
                    '& .Mui-selected': {
                      bgcolor: 'rgba(50, 130, 139, 0.2) !important',
                      color: '#32828B',
                    },
                  }}
                />
              </Stack>
            </Box>
          </>
        )}
      </Box>

      {/* Log Details Dialog */}
      <Dialog
        open={!!selectedLog}
        onClose={() => setSelectedLog(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            bgcolor: isDark ? '#1a1a1a' : '#ffffff',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
            fontWeight: 600,
            color: isDark ? '#ffffff' : '#0f172a',
          }}
        >
          Audit Log Details
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedLog && (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Box>
                <Typography sx={{ fontSize: '0.75rem', color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', mb: 0.5 }}>
                  ID
                </Typography>
                <Typography sx={{ fontFamily: 'monospace', fontSize: '0.85rem', color: isDark ? '#ffffff' : '#0f172a' }}>
                  {selectedLog.id}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.75rem', color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', mb: 0.5 }}>
                  Timestamp
                </Typography>
                <Typography sx={{ fontSize: '0.85rem', color: isDark ? '#ffffff' : '#0f172a' }}>
                  {formatTimestamp(selectedLog.timestamp)}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.75rem', color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', mb: 0.5 }}>
                  User
                </Typography>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: isDark ? '#ffffff' : '#0f172a' }}>
                  {selectedLog.username}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.75rem', color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', mb: 0.5 }}>
                  User ID
                </Typography>
                <Typography sx={{ fontFamily: 'monospace', fontSize: '0.85rem', color: isDark ? '#ffffff' : '#0f172a' }}>
                  {selectedLog.user_id}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.75rem', color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', mb: 0.5 }}>
                  Action
                </Typography>
                <Chip
                  icon={getActionIcon(selectedLog.action)}
                  label={selectedLog.action.toUpperCase()}
                  size="small"
                  color={getActionColor(selectedLog.action)}
                  sx={{ fontWeight: 600 }}
                />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.75rem', color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', mb: 0.5 }}>
                  Category
                </Typography>
                <Typography sx={{ fontSize: '0.85rem', color: isDark ? '#ffffff' : '#0f172a' }}>
                  {selectedLog.action_category || 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.75rem', color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', mb: 0.5 }}>
                  IP Address
                </Typography>
                <Typography sx={{ fontFamily: 'monospace', fontSize: '0.85rem', color: isDark ? '#ffffff' : '#0f172a' }}>
                  {selectedLog.ip_address || 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.75rem', color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', mb: 0.5 }}>
                  Request Method
                </Typography>
                <Typography sx={{ fontSize: '0.85rem', color: isDark ? '#ffffff' : '#0f172a' }}>
                  {selectedLog.request_method || 'N/A'}
                </Typography>
              </Box>
              <Box sx={{ gridColumn: '1 / -1' }}>
                <Typography sx={{ fontSize: '0.75rem', color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', mb: 0.5 }}>
                  Request Path
                </Typography>
                <Typography sx={{ fontFamily: 'monospace', fontSize: '0.85rem', color: isDark ? '#ffffff' : '#0f172a', wordBreak: 'break-all' }}>
                  {selectedLog.request_path || 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.75rem', color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', mb: 0.5 }}>
                  Status Code
                </Typography>
                <Chip
                  label={selectedLog.response_status || 'N/A'}
                  size="small"
                  color={getStatusColor(selectedLog.response_status)}
                  sx={{ fontWeight: 600 }}
                />
              </Box>
              {selectedLog.duration_ms && (
                <Box>
                  <Typography sx={{ fontSize: '0.75rem', color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', mb: 0.5 }}>
                    Duration
                  </Typography>
                  <Typography sx={{ fontSize: '0.85rem', color: isDark ? '#ffffff' : '#0f172a' }}>
                    {selectedLog.duration_ms}ms
                  </Typography>
                </Box>
              )}
              {selectedLog.user_agent && (
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <Typography sx={{ fontSize: '0.75rem', color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', mb: 0.5 }}>
                    User Agent
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: '8px',
                      bgcolor: isDark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.05)',
                      fontFamily: 'monospace',
                      fontSize: '0.8rem',
                      color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                      wordBreak: 'break-all',
                    }}
                  >
                    {selectedLog.user_agent}
                  </Box>
                </Box>
              )}
              {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <Typography sx={{ fontSize: '0.75rem', color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', mb: 0.5 }}>
                    Metadata
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: '8px',
                      bgcolor: isDark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.05)',
                      fontFamily: 'monospace',
                      fontSize: '0.8rem',
                      color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {JSON.stringify(selectedLog.metadata, null, 2)}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
            p: 2,
          }}
        >
          <Button
            onClick={() => setSelectedLog(null)}
            sx={{
              color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditLogViewer;
